// 範囲の移動・コピー (`whiteboard.region`) の適用 — ブラウザの worker / server の
// SnapshotEngine / タイムラプスの全経路から呼べる共有コード
// (docs/_wip/region-move-2026-10-15.md「決めたこと」)。
//
// 適用の手順 (順序固定):
//   (1) mode === 'move' なら、各 item の srcLayerId の src 矩形を (0,0,0,0) に消す
//   (2) 各 item の画像 (写し取った PNG を decode したもの、width×height = src の w×h) を
//       dstLayerId の (dst.x, dst.y) に置く (`EngineCore.applyImagepaste` と同じ合成、範囲外は切り捨て)
//   元と置き先が重なるときのために「消す → 置く」を逆にしてはいけない。
//   1 つでも画像が取れなければ (null / サイズ不一致 / 層が無い) 全体を適用しない
//   (部分成功は「消えて貼られない」の元)。
//
// protocol の型には依存しない (`whiteboard.region` の構造的部分型を受ける)。
const NOT_APPLIED = { applied: false, changedLayerIds: [] };
/** 適用に使える命令か (整数の矩形・w/h が正・item が 1 つ以上)。層の関係は server が検証する。 */
export const isValidRegionSpec = (spec) => {
    const { src, dst, items } = spec;
    if (!Number.isInteger(src.x + src.y + src.w + src.h + dst.x + dst.y))
        return false;
    if (src.w <= 0 || src.h <= 0)
        return false;
    if (items.length === 0)
        return false;
    return true;
};
/**
 * `whiteboard.region` を `core` に適用する。`decodedImages[i]` は `event.items[i]` の画像
 * (取れなかったものは null)。戻り値は `ApplyRegionMoveResult`。
 */
export const applyRegionMove = (core, event, decodedImages) => {
    if (!isValidRegionSpec(event))
        return NOT_APPLIED;
    const { src, dst, items, mode } = event;
    if (decodedImages.length !== items.length)
        return NOT_APPLIED;
    // 全部揃っているか先に確かめる (1 つでも欠けたら何も変えない)。
    for (let i = 0; i < items.length; i++) {
        const img = decodedImages[i];
        const item = items[i];
        if (img === null || img === undefined || item === undefined)
            return NOT_APPLIED;
        if (img.width !== src.w || img.height !== src.h)
            return NOT_APPLIED;
        if (img.rgba.length !== img.width * img.height * 4)
            return NOT_APPLIED;
        if (core.getLayer(item.dstLayerId) === undefined)
            return NOT_APPLIED;
        if (mode === 'move' && core.getLayer(item.srcLayerId) === undefined)
            return NOT_APPLIED;
    }
    const changed = new Set();
    // (1) 消す
    if (mode === 'move') {
        for (const item of items) {
            core.clearRegion(item.srcLayerId, src.x, src.y, src.w, src.h);
            changed.add(item.srcLayerId);
        }
    }
    // (2) 置く
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const img = decodedImages[i];
        if (item === undefined || img === null || img === undefined)
            continue;
        core.applyImagepaste({
            layerId: item.dstLayerId,
            rgba: img.rgba,
            width: img.width,
            height: img.height,
            x: dst.x,
            y: dst.y,
        });
        changed.add(item.dstLayerId);
    }
    return { applied: true, changedLayerIds: [...changed].sort((a, b) => a - b) };
};
const clampRect = (x, y, w, h, canvasW, canvasH) => {
    const x0 = Math.max(0, x);
    const y0 = Math.max(0, y);
    const x1 = Math.min(canvasW - 1, x + w - 1);
    const y1 = Math.min(canvasH - 1, y + h - 1);
    if (x1 < x0 || y1 < y0)
        return null;
    return { x0, y0, x1, y1 };
};
/**
 * 命令が絵を変える (層, 矩形) の一覧。矩形の作り直し (`UndoBaselineStore.repairRegion`) と
 * conflict-aware Undo の衝突判定 (server の `laterRasterEventsOnLayer` 相当) は、
 * 「src 矩形 ∪ dst 矩形」の両方を見る必要があるので、ここに両方を出す。
 * copy は src を消さないので dst だけ。キャンバス外に完全にはみ出す矩形は含めない。
 */
export const regionMoveFootprints = (spec, canvasW, canvasH) => {
    const out = [];
    const srcRect = clampRect(spec.src.x, spec.src.y, spec.src.w, spec.src.h, canvasW, canvasH);
    const dstRect = clampRect(spec.dst.x, spec.dst.y, spec.src.w, spec.src.h, canvasW, canvasH);
    for (const item of spec.items) {
        if (spec.mode === 'move' && srcRect !== null) {
            out.push({ layerId: item.srcLayerId, rect: srcRect, role: 'src' });
        }
        if (dstRect !== null)
            out.push({ layerId: item.dstLayerId, rect: dstRect, role: 'dst' });
    }
    return out;
};
//# sourceMappingURL=regionMove.js.map